import { format, isValid, parseISO } from 'date-fns';

/**
 * Format a JS Date as ISO date (YYYY-MM-DD).
 * Ports legacy formatDate from obd_frontend/Helpers/utils.js.
 */
export function formatDate(date: Date | null | undefined): string {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd');
}

/**
 * Format a backend datetime value as a localized, human-readable string.
 *
 * Wire formats observed in the wild:
 *   - "YYYY-MM-DD HH:mm:ss"  (Java date-formatter default)
 *   - "YYYY-MM-DDTHH:mm:ss[.SSSZ]"  (ISO)
 *   - epoch-ms number  (Java Long, e.g. 1717920000000)
 *   - pre-formatted English string  ("Jun 9, 2026 2:30:45 PM")
 *
 * Returns "-" only when the input is genuinely missing. If we can't
 * parse the value (last case above), we return it as-is so the user
 * sees SOMETHING instead of a blank cell. Ports the legacy helper but
 * is more lenient about wire variations.
 */
export function formatDateTime(cell: string | number | null | undefined): string {
  if (cell === null || cell === undefined || cell === '') return '-';

  // Numeric epoch — Java Long milliseconds (or seconds if it looks too small).
  if (typeof cell === 'number') {
    const ms = cell > 1e12 ? cell : cell * 1000;
    const date = new Date(ms);
    return Number.isNaN(date.getTime())
      ? String(cell)
      : format(date, 'MMM dd, yyyy h:mm:ss a');
  }

  // Try ISO-style first ("YYYY-MM-DD HH:mm:ss" → "YYYY-MM-DDTHH:mm:ss").
  const iso = parseISO(cell.replace(' ', 'T'));
  if (isValid(iso)) return format(iso, 'MMM dd, yyyy h:mm:ss a');

  // Fall back to native Date for less-structured strings ("Jun 9, 2026 …").
  const native = new Date(cell);
  if (!Number.isNaN(native.getTime())) {
    return format(native, 'MMM dd, yyyy h:mm:ss a');
  }

  // Last resort — show the raw string so the cell isn't blank.
  return cell;
}

/**
 * Convert "HH:mm" or "YYYY-MM-DD HH:mm" to 12-hour "h:mm AM/PM" form.
 * Ports legacy convertToAMPM.
 */
export function convertToAMPM(timeString: string | null | undefined): string {
  if (!timeString) return '';
  const timePart = timeString.includes(' ') ? (timeString.split(' ')[1] ?? '') : timeString;
  const parts = timePart.split(':');
  const hours = parseInt(parts[0] ?? '0', 10);
  let minutes: string | number = parseInt(parts[1] ?? '0', 10);

  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  minutes = minutes < 10 ? `0${minutes}` : minutes;

  return `${displayHours}:${minutes} ${ampm}`;
}

export function isExpired(expiryDate: string | null | undefined): boolean {
  if (!expiryDate) return false;
  const exp = parseISO(expiryDate.replace(' ', 'T'));
  if (!isValid(exp)) return false;
  return exp < new Date();
}
